import BaseScene from '@Scenes/BaseScene';
import Delay from '@Utils/Time/Delay';
import Network from '@Networks/Localhost';

import * as Scenes from '@Config/scenes';
import * as Tracks from '@Config/tracks';
import * as WebFont from 'webfontloader';

import Game from '@Game';

/**
 * Main scene
 * @author charlie.bodman@kanoapps.com
 */
class LoadingScene extends BaseScene
{
    private progressBar: Phaser.GameObjects.Graphics;
    private progressText: Phaser.GameObjects.Text;

    /**
     * Constructor
     */
    constructor()
    {
        super(Scenes.LOADING);
    }

    /**
     * Preload method as part of the Phaser Scene lifecycle
     */
    public preload()
    {
        this.createLoadingBar();

        this.load.setBaseURL(process.env.FRJ_ASSETS_BASE_URL);
        this.load.setPath('assets/');
        this.load.on('progress', this.setProgress.bind(this));

        this.load.image('logo', 'logo.png');
        this.load.image('background', 'background.png');
        this.load.image('right_arrow', 'right_arrow.png');
        this.load.image('left_arrow', 'left_arrow.png');
        this.load.image('challenge_icon', 'share.png');
        this.load.image('leaderboard_icon', 'leaderboards.png');
        this.load.image('score_icon', 'score.png');
        this.load.image('particle', 'particle.png');
        this.load.image('player_image', 'player_image.jpg');
        this.load.image('watch_video_icon', 'ic_watch_video.png');
        this.load.image('watch_video_icon_hover', 'ic_watch_video_hover.png')
        this.load.image('splash', 'splash.png');
        this.load.image('btn_grey', 'btn_grey.png');
        this.load.image('btn_back', 'btn_back.png');
        this.load.image('add_to_home_icon', 'add_to_home_screen.png');
        this.load.image('touch_icon', 'touch_icon.png');
        this.load.image('share_score_icon', 'share_score.png');
        this.load.image('restart_icon', 'restart.png');
        this.load.image('try_again_button', 'try_again_button.png');
        this.load.image('play_now_black', 'play-now-black.png');
        this.load.image('close_black', 'close-btn.png');
        this.load.image('feature_not_available_splash', 'feature-not-available.png');
        this.load.image('play_with_friends_button', 'play-with-friends-button.png');
        this.load.image('challenge_random_button', 'challenge-random-button.png');
        this.load.image('play_solo_button', 'play-solo-button.png');

        this.load.audio('trick', 'trick.mp3');
        this.load.atlas('tutorial_lean_back', 'lean-back.png', 'lean-back.json');
        this.load.atlas('tutorial_lean_forward', 'lean-forward.png', 'lean-forward.json');
        this.load.atlas('keyboard_controls', 'keyboard_controls.png', 'keyboard_controls.json');

        this.load.text(Tracks.TRACK_100_JUMPS, '100Jumps.txt');
    }

    private createLoadingBar()
    {
        const gameSize = this.getGameSize();

        const width = gameSize.width;
        const height = gameSize.height;

        var progressBox = this.add.graphics();
        var progressBar = this.add.graphics();

        progressBox.fillStyle(0x222222, 0.8);
        progressBox.fillRect(0, 0, 320, 50);
        progressBox.x = (width / 2) - 160;
        progressBox.y = (height / 2) - 25;

        progressBar.x = (width / 2) - 150;
        progressBar.y = (height / 2) - 15;


        const loadingText = this.make.text({
            x: width / 2,
            y: height / 2 - 50,
            text: 'Loading...',
            style: {
                font: '20px monospace',
                fill: '#FFFFFF'
            },
            align:'center'
        });

        loadingText.setOrigin(0.5, 0.5);

        const progressText = this.make.text({
            x: width / 2,
            y: height / 2,
            text: '0%',
            style: {
                font: '18px monospace',
                fill: '#04A8F1'
            }
        });
        progressText.setOrigin(0.5, 0.5);

        const assetText = this.make.text({
            x: width / 2,
            y: height / 2 + 50,
            text: '',
            style: {
                font: '18px monospace',
                fill: '#ffffff'
            }
        });

        assetText.setOrigin(0.5, 0.5);

        this.load.on('fileprogress', function (file)
        {
            assetText.setText('Loading asset: ' + file.key);
        });

        this.progressText = progressText;
        this.progressBar = progressBar;
    }

    /**
     * Sets the position of the progress bar
     */
    private setProgress(value: number): void
    {
        const progressText = this.progressText;
        const progressBar = this.progressBar;

        progressText.setText(parseInt((value * 100).toString()) + '%');
        progressBar.clear();
        progressBar.fillStyle(0x04A8F1, 1);
        progressBar.fillRect(0, 0 , 300 * value, 30);
        Network.setLoadingProgress((value * 100) / 2 + 20);
    }

    /**
     * Create method as part of the Phaser Scene lifecycle
     */
    public async create(): Promise<void>
    {
        WebFont.load({ google: { families: ['Permanent Marker'] } });

        Game.freerider.load(this.cache.text.get(Tracks.TRACK_100_JUMPS));

        Game.setLoadingProgress(100);

        await Game.startGame();

        // Delay for 1 second, this is to prevent sizing race condition.
        await Delay(500);

        this.scene.start(Scenes.START_SCREEN);

        Game.freerider.show();
    }

}

export default LoadingScene;